/**
 * Late-cancellation window.
 *
 * A visit is late when it is canceled with strictly less than the notice
 * window still to go. Exactly the window — 24 hours to the minute — is on
 * time. The comparison is on absolute instants: `starts_at` is already UTC,
 * so a daylight-saving jump between the cancel and the visit is just a
 * different number of wall-clock hours, which is what "24 hours' notice"
 * means.
 */

import { formatCents } from './calculations/pricing.ts';
import { formatExactAmount, isChargeableAmount, minimumChargeCents, roundToCurrencyStep } from './currency.ts';

export const LATE_CANCEL_NOTICE_HOURS_DEFAULT = 24;
export const LATE_CANCEL_FEE_CENTS_DEFAULT = 5000;
export const LATE_CANCEL_NOTICE_HOURS_MIN = 1;
export const LATE_CANCEL_NOTICE_HOURS_MAX = 336;

const HOUR_MS = 60 * 60 * 1000;

export function isInsideLateCancelWindow(startsAtIso: string, cancelledAtIso: string, noticeHours: number): boolean {
  const start = Date.parse(startsAtIso);
  const cancelled = Date.parse(cancelledAtIso);
  if (!Number.isFinite(start) || !Number.isFinite(cancelled)) return false;
  if (!Number.isFinite(noticeHours) || noticeHours <= 0) return false;
  return cancelled > start - noticeHours * HOUR_MS;
}

/** Why a stored late fee cannot be collected by card, or null when it can. */
export function lateCancelFeeAmountError(feeCents: number, currencyCode: string): string | null {
  if (feeCents <= 0) return 'Choose a fee amount greater than zero.';
  if (isChargeableAmount(feeCents, currencyCode)) return null;
  const minimum = minimumChargeCents(currencyCode);
  if (feeCents < minimum) {
    return `The fee must be at least ${formatCents(minimum, currencyCode)} to collect by card.`;
  }
  return `${currencyCode} is billed in whole units, so ${formatExactAmount(feeCents, currencyCode)} cannot be charged. The nearest chargeable amount is ${formatCents(
    roundToCurrencyStep(feeCents, currencyCode),
    currencyCode,
  )}.`;
}
